<template>
  <div class="deadline-edit-filter">
    <div class="deadline-edit-filter__date">
      <div class="deadline-edit-filter__date--from">
        <h4>Valid From</h4>
        <form-label v-if="$route.name === 'deadline-edit'">
          Valid From will be set from today on in case of edit.
        </form-label>
        <form-datepicker
          v-model="dateValidFrom"
          class="spl-datepicker__create-edit"
          :min="new Date().toISOString().substr(0, 10)"
          @input="handleDateInput($event, '', 'valid_from')" />
      </div>
      <div class="deadline-edit-filter__date--to">
        <h4>Valid To</h4>
        <form-label>Choose until when this deadline will be available.<br>Choose max date if this deadline should not expire</form-label>
        <form-datepicker
          v-model="defaultFilter.Valid_To"
          class="spl-datepicker__create-edit"
          :min="new Date().toISOString().substr(0, 10)"
          @input="handleDateInput($event, '', 'valid_to')" />
      </div>
      <div class="deadline-edit-filter__date--open-deadline">
        <h4>Open Deadline</h4>
        <form-label>Pickup is done day 0. Open Deadline means that we cannot deliver before this deadline. Parcels will end up as Early in the system.<br>Leave empty if no deadline</form-label>
        <div class="deadline-edit-deadlines">
          <div class="deadline-edit-deadlines__inputs">
            <form-label>Day</form-label>
            <form-input
              v-model="defaultDeadline.Open_Deadline.Day"
              type="number"
              :min="0"
              @input="handleDateInput($event, 'day', 'open_deadline')" />
          </div>
          <div class="deadline-edit-deadlines__inputs">
            <form-label>Hour</form-label>
            <form-input
              v-model="defaultDeadline.Open_Deadline.Hour"
              type="number"
              :min="0"
              @input="handleDateInput($event, 'hour', 'open_deadline')" />
          </div>
          <div class="deadline-edit-deadlines__inputs">
            <form-label>Minute</form-label>
            <form-input
              v-model="defaultDeadline.Open_Deadline.Minute"
              type="number"
              :min="0"
              @input="handleDateInput($event, 'minute', 'open_deadline')" />
          </div>
        </div>
      </div>
      <div class="deadline-edit-filter__date--close-deadline">
        <h4>Close Deadline</h4>
        <form-label>Pickup is done day 0. Close Deadline means that we cannot deliver after this deadline. Parcels will end up as Late in the system.</form-label>
        <div class="deadline-edit-deadlines">
          <div class="deadline-edit-deadlines__inputs">
            <form-label>Day</form-label>
            <form-input
              v-model="defaultDeadline.Close_Deadline.Day"
              type="number"
              :min="0"
              @input="handleDateInput($event, 'day', 'close_deadline')" />
          </div>
          <div class="deadline-edit-deadlines__inputs">
            <form-label>Hour</form-label>
            <form-input
              v-model="defaultDeadline.Close_Deadline.Hour"
              type="number"
              :min="0"
              @input="handleDateInput($event, 'hour', 'close_deadline')" />
          </div>
          <div class="deadline-edit-deadlines__inputs">
            <form-label>Minute</form-label>
            <form-input
              v-model="defaultDeadline.Close_Deadline.Minute"
              type="number"
              :min="0"
              @input="handleDateInput($event, 'minute', 'close_deadline')" />
          </div>
        </div>
      </div>
    </div>
    <div class="deadline-edit-filter__fields">
      <div style="grid-column: 1; grid-row: 1;">
        <form-label>Country</form-label>
        <country-select
          v-model="defaultFilter.Country_ID"
          :options="countryOptions"
          data-cy="edit-deadline-country"
          track-by="value"
          label="label"
          placeholder="Country" /><br>
      </div>
      <div style="grid-column: 2; grid-row: 1;">
        <form-label>Customer</form-label>
        <customer-select
          v-model="defaultFilter.Customer_ID"
          :options="customerOptions"
          data-cy="edit-deadline-customer"
          track-by="value"
          label="label"
          placeholder="Customer" /><br>
      </div>
      <div style="grid-column: 3; grid-row: 1;">
        <form-label>Service Level</form-label>
        <service-level-select
          v-model="selectedServiceLevel"
          :options="serviceLevelOptions"
          data-cy="edit-deadline-service-level"
          track-by="value"
          label="label"
          placeholder="Service Level" />
      </div>
    </div>
    <h3 />
    <div class="deadline-edit-filter__postal">
      <div>
        <form-label>Postal Code From</form-label>
        <form-input v-model="defaultFilter.From_Postal_Code" data-cy="edit-deadline-postal-code-from" />
      </div>
      <div>
        <form-label>Postal Code To</form-label>
        <form-input v-model="defaultFilter.To_Postal_Code" data-cy="edit-deadline-postal-code-to" />
      </div>
      <div>
        <form-label>City</form-label>
        <form-input v-model="defaultFilter.City" data-cy="edit-deadline-city" />
      </div>
    </div>
    <div class="deadline-edit-filter__weekend-delivery" style="display: block">
      <form-label>Weekend Delivery</form-label>
      <form-toggle-switch
        v-model="defaultFilter.Weekend_Delivery"
        data-cy="edit-deadline-weekend-delivery"
        text-unchecked="No"
        text-checked="Yes" />
    </div>
    <div class="deadline-edit-filter__delivery-days">
      <form-label v-for="(day, index) in days" :key="index">
        {{ day }}
        <form-toggle-switch
          v-model="selectedDays[index]"
          data-cy="edit-deadline-delivery-days"
          text-unchecked="No"
          text-checked="Yes"
          @input="onValueSwitch(selectedDays[index], index)" />
      </form-label>
    </div>
  </div>
</template>

<script lang="ts">
import Helpers from '@/helpers.vue'
import constants from '@/constants'
import { set, cloneDeep } from 'lodash'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { Deadline } from '@/webApi/DataModels'
import moment from 'moment'

export default {
  mixins: [Helpers],
  data () {
    return {
      defaultDeadline: {
        Close_Deadline: {
          Day: 0,
          Hour: 0,
          Minute: 0
        },
        Open_Deadline: {
          Day: 0,
          Hour: 0,
          Minute: 0
        }
      },
      dateValidFrom: moment().toISOString(true),
      disabledDates: { to: moment().toISOString(true) },
      defaultFilter: {
        Service_Level_ID: {},
        Customer_ID: {},
        Country_ID: {},
        From_Postal_Code: '',
        To_Postal_Code: '',
        City: '',
        Valid_From: moment().toISOString(true),
        Valid_To: moment().toISOString(true),
        Weekend_Delivery: false
      },
      constants,
      selectedCountry: {},
      selectedCustomer: {},
      selectedServiceLevel: {},
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      selectedDays: []
    }
  },
  computed: {
    ...mapGetters('serviceLevel', ['serviceLevelOptions']),
    ...mapGetters('country', ['countryOptions']),
    ...mapGetters('customer', ['customerOptions']),
    ...mapState('deadline', ['currentEditDeadline']),
    checkboxValues () {
      return this.stringToBoolArray(this.currentEditDeadline.Delivery)
    }
  },
  watch: {
    selectedCountry: function () {
      if (this.selectedCountry !== -1) {
        this.defaultFilter.Country_ID = this.selectedCountry
        this.setCurrentEditDeadline(set(cloneDeep(this.currentEditDeadline), 'Deadline_Filter', this.defaultFilter))
      }
    },
    selectedCustomer: function () {
      if (this.selectedCustomer !== -1) {
        this.defaultFilter.Customer_ID = this.selectedCustomer
      } else {
        this.defaultFilter.Customer_ID = {}
      }
      this.setCurrentEditDeadline(set(cloneDeep(this.currentEditDeadline), 'Deadline_Filter', this.defaultFilter))
    },
    selectedServiceLevel: function () {
      if (this.selectedServiceLevel !== -1) {
        this.defaultFilter.Service_Level_ID = this.selectedServiceLevel
        this.setCurrentEditDeadline(set(cloneDeep(this.currentEditDeadline), 'Deadline_Filter', this.defaultFilter))
      }
    },
    defaultFilter: {
      handler: function () {
        // update the deadline filter everytime the parcel filter changes
        this.setCurrentEditDeadline(set(cloneDeep(this.currentEditDeadline), 'Deadline_Filter', this.defaultFilter))
      },
      deep: true
    }
  },
  mounted () {
    this.selectedDays = this.checkboxValues
    // get the values out of the current deadline (when editing for example) and transform them into a usable format
    this.defaultFilter = cloneDeep(this.currentEditDeadline)
    if (this.defaultFilter.Country_ID < 0) {
      this.defaultFilter.Country_ID = {}
    }
    if (this.defaultFilter.Customer_ID < 0) {
      this.defaultFilter.Customer_ID = {}
    }
    if (this.defaultFilter.Service_Level_ID < 0) {
      this.defaultFilter.Service_Level_ID = null
    }
    if (this.currentEditDeadline.Country_ID !== -1) {
      this.set_Country_Dropdown_By_ID(this.currentEditDeadline.Country_ID)
    }
    if (this.currentEditDeadline.Customer_ID !== -1) {
      this.set_Customer_Dropdown_By_ID(this.currentEditDeadline.Customer_ID)
    }
    if (this.currentEditDeadline.Service_Level_ID !== -1) {
      this.set_Service_Level_Dropdown_By_ID(this.currentEditDeadline.Service_Level_ID)
    }
    if (this.currentEditDeadline.Open_Deadline_Day > 0 && this.currentEditDeadline.Open_Deadline_Time !== null) {
      var openDeadlineValue = this.currentEditDeadline.Open_Deadline_Time
      var hm = openDeadlineValue.split(':')
      if (hm.length > 1) {
        this.defaultDeadline.Open_Deadline.Hour = this.leftPad(hm[0], 2)
        this.defaultDeadline.Open_Deadline.Minute = this.leftPad(hm[1], 2)
      } else {
        this.defaultDeadline.Open_Deadline.Hour = this.leftPad(hm[0], 2)
        this.defaultDeadline.Open_Deadline.Minute = '00'
      }
      this.defaultDeadline.Open_Deadline.Day = this.currentEditDeadline.Open_Deadline_Day
    } else {
      this.defaultFilter.Open_Deadline_Day = '0'
      this.defaultFilter.Open_Deadline_Time = '00:00'
    }
    if (this.currentEditDeadline.Close_Deadline_Time !== 0 && this.currentEditDeadline.Close_Deadline_Time !== '') {
      var closeDeadlineValue = this.currentEditDeadline.Close_Deadline_Time
      hm = closeDeadlineValue.split(':')
      if (hm.length > 1) {
        this.defaultDeadline.Close_Deadline.Hour = this.leftPad(hm[0], 2)
        this.defaultDeadline.Close_Deadline.Minute = this.leftPad(hm[1], 2)
        closeDeadlineValue = this.convertMinutes(closeDeadlineValue.split(':')[0], closeDeadlineValue.split(':')[1])
      } else {
        this.defaultDeadline.Close_Deadline.Hour = this.leftPad(hm[0], 2)
        this.defaultDeadline.Close_Deadline.Minute = '00'
      }
      this.defaultDeadline.Close_Deadline.Day = this.currentEditDeadline.Close_Deadline_Day
    } else {
      this.defaultFilter.Close_Deadline_Day = '1'
      this.defaultFilter.Close_Deadline_Time = '00:00'
    }
  },
  methods: {
    ...mapMutations('deadline', ['setCurrentEditDeadline']),
    stringToBoolArray (str) {
      if (str) {
        return str.split('').map(char => char === '1')
      } else {
        return [true, true, true, true, true, false, false]
      }
    },
    onValueSwitch (value, index) {
      this.selectedDays[index] = value
      this.setDeliveryDaysOnCurrentDeadline()
    },
    onCountriesSelected (countries) {
      this.defaultFilter.Country_ID = countries
    },
    set_Country_Dropdown_By_ID (countryID) {
      for (var i in this.countryOptions) {
        if (this.countryOptions[i].value === countryID) {
          this.selectedCountry = this.countryOptions[i]
          return
        }
      }
    },
    set_Customer_Dropdown_By_ID (customerID) {
      for (var i in this.customerOptions) {
        if (this.customerOptions[i].value === customerID) {
          this.selectedCustomer = this.customerOptions[i]
          return
        }
      }
    },
    set_Service_Level_Dropdown_By_ID (serviceLevelID) {
      for (var i in this.serviceLevelOptions) {
        if (this.serviceLevelOptions[i].value === serviceLevelID) {
          this.selectedServiceLevel = this.serviceLevelOptions[i]
          return
        }
      }
    },
    onCustomersSelected (customers) {
      this.defaultFilter.Customer_ID = customers
    },
    onServiceLevelSelected (serviceLevel) {
      this.defaultFilter.Service_Level_ID = serviceLevel
    },
    // Handles input changes in the date fiels,
    // only one selection is possible so it
    // resets the selected date object if another
    // type is selected
    handleDateInput (value, type, specifier) {
      if (specifier === 'open_deadline') {
        if (type === 'day') { this.defaultDeadline.Open_Deadline.Day = value }
        if (type === 'hour') { this.defaultDeadline.Open_Deadline.Hour = value }
        if (type === 'minute') { this.defaultDeadline.Open_Deadline.Minute = value }
        this.defaultFilter.Open_Deadline_Day = this.defaultDeadline.Open_Deadline.Day
        this.defaultFilter.Open_Deadline_Time = this.leftPad(this.defaultDeadline.Open_Deadline.Hour, 2) + ':' + this.leftPad(this.defaultDeadline.Open_Deadline.Minute, 2)
      } else if (specifier === 'valid_from') {
        this.defaultFilter.Valid_From = this.formatDate(value, false, 'YYYY-MM-DD')
      } else if (specifier === 'valid_to') {
        this.defaultFilter.Valid_To = this.formatDate(value, false, 'YYYY-MM-DD')
      } else {
        if (type === 'day') { this.defaultDeadline.Close_Deadline.Day = value }
        if (type === 'hour') { this.defaultDeadline.Close_Deadline.Hour = value }
        if (type === 'minute') { this.defaultDeadline.Close_Deadline.Minute = value }
        this.defaultFilter.Close_Deadline_Day = this.defaultDeadline.Close_Deadline.Day
        this.defaultFilter.Close_Deadline_Time = this.leftPad(this.defaultDeadline.Close_Deadline.Hour, 2) + ':' + this.leftPad(this.defaultDeadline.Close_Deadline.Minute, 2)
      }

      this.writeDateFilter()
      this.$forceUpdate()
    },
    setDeliveryDaysOnCurrentDeadline () {
      var deadline:Deadline = cloneDeep(this.currentEditDeadline) as Deadline
      deadline.Delivery = this.selectedDays.map((value) => {
        return value ? '1' : '0'
      }).join('')
      this.setCurrentEditDeadline(deadline)
    },
    writeDateFilter () {
      // Everytime the date filter is changed write it into a usable format for storing
      var deadline:Deadline = cloneDeep(this.currentEditDeadline) as Deadline
      deadline.Open_Deadline_Day = this.defaultFilter.Open_Deadline_Day
      deadline.Close_Deadline_Day = this.defaultFilter.Close_Deadline_Day
      deadline.Open_Deadline_Day = this.defaultFilter.Open_Deadline_Day
      deadline.Close_Deadline_Time = this.defaultFilter.Close_Deadline_Time
      this.setCurrentEditDeadline(deadline)
    }
  }
}
</script>

<style lang="scss">
  @import '../../../variables.scss';
  div.deadline-edit-deadlines {
    float: left;
    display: flex;
    width: 99%;
    &__inputs {
      width: 33%;
    }
  }

  .spl-datepicker__create-edit {
    position: absolute;
    bottom: 0;
  }

  .deadline-edit-filter {
    margin-bottom: 100px;
    &__date {
      display: grid;
      grid-template-columns: 24% 24% 24% 24%;
      grid-gap: 7px;
      width: 100%;
      padding-top: 1%;
      position: relative;

      &--active {
        background-color: $grey_80 !important;
      }

      &--close-deadline, &--open-deadline {
        position: relative;
      }

      div {
        background-color: $grey_95;
        border-radius: 5px;
        padding: 3px;
      }

      .spl-checkbox-group__item__label {
        margin-bottom: 10px;
      }
    }

    &__deadline {
      float: left;
      display: flex;
    }

    &__delivery-days {
      display: inline-flex;
      grid-template-columns: 14% 14% 14% 14% 14% 14% 14%;
      grid-gap: 7px;
      width: 100%;
      padding-top: 1%;
      div {
        margin-bottom: 10px;
      }
    }

    &__fields, &__postal, &__references, &__weekend-delivery {
      display: grid;
      grid-template-columns: 24% 24% 24% 24%;
      grid-gap: 7px;
      width: 100%;
      padding-top: 1%;
      div {
        margin-bottom: 10px;
      }
    }
    &__fields:last-child, &__postal:last-child, &__references:last-child, &__delivery-days:last-child {
      padding-bottom: 100px;
    }
  }
</style>
